import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Switch, Space, Card, message, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import {
  listNfsLocations, createNfsLocation, updateNfsLocation, deleteNfsLocation,
  NfsLocation, NfsRole,
} from '../../services/workspaceApi';

const ROLE_OPTIONS: { value: NfsRole; label: string }[] = [
  { value: 'tools', label: 'tools' },
  { value: 'labs', label: 'labs' },
  { value: 'pdk', label: 'pdk' },
  { value: 'home', label: 'home' },
];

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

const NfsLocationsPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const role = useSelector((s: RootState) => s.auth.user?.role);
  const canWrite = role === 'itadmin' || role === 'superadmin';

  const [rows, setRows] = useState<NfsLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<NfsLocation | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNfsLocations();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load NFS locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ readOnly: true, role: 'tools' });
    setModalOpen(true);
  };

  const openEdit = (record: NfsLocation) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await updateNfsLocation(editing.id, values);
        message.success('Updated');
      } else {
        await createNfsLocation(values);
        message.success('Created');
      }
      setModalOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      const m = errMsg(e, 'Save failed');
      if (m) message.error(m);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record: NfsLocation) => {
    Modal.confirm({
      title: 'Delete NFS location?',
      content: `Remove "${record.code}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteNfsLocation(record.id);
          message.success('Deleted');
          await load();
        } catch (e) {
          message.error(errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 120 },
    { title: 'Role', dataIndex: 'role', key: 'role', width: 90 },
    { title: 'NFS Server', dataIndex: 'nfsServer', key: 'nfsServer' },
    { title: 'NFS Path', dataIndex: 'nfsPath', key: 'nfsPath', ellipsis: true },
    { title: 'Mount Path', dataIndex: 'mountPath', key: 'mountPath', ellipsis: true },
    {
      title: 'Read-only',
      dataIndex: 'readOnly',
      key: 'readOnly',
      width: 90,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    canWrite
      ? {
          title: 'Actions',
          key: 'actions',
          width: 140,
          render: (_: unknown, record: NfsLocation) => (
            <Space>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                Edit
              </Button>
              <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                Delete
              </Button>
            </Space>
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="NFS Locations"
            purpose="Catalog of tools / labs / PDK / home NFS mounts for workspace sessions."
            actions={
              canWrite ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                  Add Location
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">
              NFS Locations
            </Typography.Title>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add Location
              </Button>
            )}
          </div>
        )}

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns as any}
          dataSource={rows}
          pagination={false}
        />
      </Card>

      <Modal
        title={editing ? 'Edit NFS Location' : 'Add NFS Location'}
        open={modalOpen}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={editing ? 'Save' : 'Add'}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. tools-main" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Required' }]}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name="nfsServer" label="NFS Server" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. swstech.trg.vlsiexpert.in" />
          </Form.Item>
          <Form.Item name="nfsPath" label="NFS Path" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. /export/tools" />
          </Form.Item>
          <Form.Item name="mountPath" label="Mount Path" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. /tools" />
          </Form.Item>
          <Form.Item name="readOnly" label="Read-only" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NfsLocationsPage;
